/**
 * ExplorerInfoPanel.tsx
 * Right sidebar showing entity details
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import type { ExplorerNode } from '../../../types/entity-explorer.types';
import { ENTITY_TYPE_LABELS } from '../../../types/entity-explorer.types';
import { styles } from './entityExplorer.styles';

interface ExplorerInfoPanelProps {
  node: ExplorerNode | null;
}

/**
 * Info panel showing details about selected node
 * Only shows for version items (kind === 'version')
 */
export function ExplorerInfoPanel({ node }: ExplorerInfoPanelProps) {
  if (!node) {
    return (
      <Box sx={styles.infoPanelContainer as any}>
        <Box sx={styles.infoPanelEmpty as any}>
          Select a file to see details
        </Box>
      </Box>
    );
  }

  // Only show info panel for version items
  if (node.kind !== 'version') {
    return (
      <Box sx={styles.infoPanelContainer as any}>
        <Box sx={styles.infoPanelEmpty as any}>
          Select a file to see details
        </Box>
      </Box>
    );
  }

  const getCreatedByName = (createdBy: any): string => {
    if (!createdBy) return '—';
    // If object, extract name only
    if (typeof createdBy === 'object') {
      return createdBy.name || createdBy.fullName || createdBy.username || '—';
    }
    // If string (ObjectId), return as-is
    if (typeof createdBy === 'string') return createdBy;
    return '—';
  };

  // Map entityType to display label
  const getTypeLabel = (entityType: string): string => {
    return ENTITY_TYPE_LABELS[entityType as keyof typeof ENTITY_TYPE_LABELS] || entityType;
  };

  // Helper to strip HTML tags from description
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Get description and tags - now populated from experiment by API service
  const description = (node.raw?.description || '') as string;
  const cleanDescription = description ? stripHtmlTags(description) : '';
  const tags = (node.raw?.tags || []) as string[];

  return (
    <Box sx={styles.infoPanelContainer as any}>
      {/* Section Header */}
      <Typography sx={{ 
        fontSize: '1rem', 
        fontWeight: 600, 
        mb: 2,
        pb: 1,
        borderBottom: '2px solid #2196f3'
      }}>
        Information
      </Typography>

      {/* Name */}
      <Box sx={styles.infoPanelField as any}>
        <Typography sx={styles.infoPanelLabel as any}>Name</Typography>
        <Typography sx={styles.infoPanelValue as any}>
          {node.name}
        </Typography>
      </Box>

      {/* ID - Show System ID (uid) instead of MongoDB ObjectId */}
      <Box sx={styles.infoPanelField as any}>
        <Typography sx={styles.infoPanelLabel as any}>ID</Typography>
        <Typography sx={styles.infoPanelValue as any}>
          {(node.raw?.uid || node.raw?.externalUid || node.id || '—') as string}
        </Typography>
      </Box>

      {/* Type */}
      <Box sx={styles.infoPanelField as any}>
        <Typography sx={styles.infoPanelLabel as any}>Type</Typography>
        <Typography sx={styles.infoPanelValue as any}>
          {getTypeLabel(node.entityType)}
        </Typography>
      </Box>

      {/* Created By */}
      {node.createdBy && (
        <Box sx={styles.infoPanelField as any}>
          <Typography sx={styles.infoPanelLabel as any}>
            Created By
          </Typography>
          <Typography sx={styles.infoPanelValue as any}>
            {getCreatedByName(node.createdBy)}
          </Typography>
        </Box>
      )}

      {/* Created On */}
      {node.createdAt && (
        <Box sx={styles.infoPanelField as any}>
          <Typography sx={styles.infoPanelLabel as any}>
            Created On
          </Typography>
          <Typography sx={styles.infoPanelValue as any}>
            {new Date(node.createdAt).toLocaleString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            })}
          </Typography>
        </Box>
      )}

      {/* Description */}
      <Box sx={styles.infoPanelField as any}>
        <Typography sx={styles.infoPanelLabel as any}>
          Description
        </Typography>
        <Typography sx={styles.infoPanelValue as any}>
          {cleanDescription || '—'}
        </Typography>
      </Box>

      {/* Tags */}
      <Box sx={styles.infoPanelField as any}>
        <Typography sx={styles.infoPanelLabel as any}>
          Tags
        </Typography>
        {tags && Array.isArray(tags) && tags.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {tags.map((tag: string, idx: number) => (
              <Typography 
                key={idx}
                sx={{ 
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: '0.85rem'
                }}
              >
                {tag}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={styles.infoPanelValue as any}>
            No tags
          </Typography>
        )}
      </Box>

    </Box>
  );
}
